/**
 * The referential API, typed at the edge of this package.
 *
 * Hand-written rather than pulled from the generated OpenAPI types, for the
 * reason the other features give: the generated schema is the authority and CI
 * fails on drift, but reaching into `components["schemas"][...]` at every call
 * site makes the code unreadable.
 *
 * One thing to notice about `StockLevel`: it carries `reserved` and
 * `forecast`, which a customer must never see. Every endpoint that returns it
 * is behind `referential.stock.read`, and the customer-facing shape is a band
 * — see the backend's `StockService.disclose`. This package is the back
 * office, so it gets the full figures.
 */

import type { ApiClient } from "@hardauto/core-api";

const BASE = "/api/v1/referential";

export type PublicationStatus = "draft" | "published" | "archived";
export type StockDisclosure = "exact" | "bands" | "hidden";
export type AttributeSource = "derived" | "manual" | "imported";
export type SiteKind = "warehouse" | "store";

export interface ArticleAttribute {
  key: string;
  value: string;
  /**
   * Where the value came from, and therefore what may overwrite it. A
   * `derived` value is replaced by the next parse; a `manual` one never is.
   * The screen shows this because an operator correcting an attribute needs to
   * know whether their correction will survive.
   */
  source: AttributeSource;
  is_filterable: boolean;
}

export interface Article {
  id: string;
  reference: string;
  designation: string;
  brand: string;
  family_code: string;
  sales_unit: string;
  base_price: string;
  status: PublicationStatus;
  stock_disclosure: StockDisclosure;
  /** The distributor's own cross-reference for the part. Free text. */
  reference_base: string;
  /** The supplier's code for this article. */
  code_fournisseur: string;
  ipc: string;
  /**
   * Whether the item can be sold at all, independent of `status`: an
   * inactive article disappears from ordering and stock everywhere, not
   * just its web page.
   */
  is_active: boolean;
  attributes: ArticleAttribute[];
  /**
   * Where the row came from. `portal` means somebody created it here;
   * `external` that it was pulled in. Shown on the detail screen because an
   * operator about to correct a field needs to know whether a synchronisation
   * will overwrite them.
   */
  origin: string;
  external_system: string | null;
  external_ref: string | null;
  sync_state: string;
  /** Fields an operator pinned. A synchronisation skips them. */
  locked_fields: string[];
  created_at: string | null;
}

export interface ArticleRow {
  article: Article;
  total_real: string;
  total_available: string;
  /** Percentage of the family's required attributes that are filled. */
  completion: number;
}

export interface ArticleList {
  items: ArticleRow[];
  /** How many match the filters, not how many are on this page. */
  total: number;
  next_cursor: string | null;
}

export interface StockLevel {
  article_reference: string;
  article_designation: string;
  site_code: string;
  site_name: string;
  /** The company owning the depot. Empty when it is attached to none. */
  company_code: string;
  /** The place hosting the depot. Empty when it is attached to none. */
  point_of_sale_code: string;
  point_of_sale_name: string;
  /** The depot's location: its place's city, or its own when it has no place. */
  city: string;
  real: string;
  reserved: string;
  forecast: string;
  available: string;
  alert_threshold: string;
  is_below_threshold: boolean;
}

export interface ArticleDetail {
  article: Article;
  stock: StockLevel[];
}

/** What a merge did. The count is what tells an operator they merged the
 * family they meant: 1 884 articles leaving `NULL` is right, four leaving
 * `CARROSSERIE1` is right, and the two would be equally silent. */
export interface MergeOutcome {
  source: string;
  target: string;
  articles_moved: number;
}

export interface Family {
  code: string;
  label: string;
  default_unit: string;
  required_attributes: string[];
  is_active: boolean;
  article_count: number;
  /**
   * The family this one was folded into, when it was. Empty otherwise.
   *
   * Carried so the screen can say "fusionnée dans LUBRIFIANT" rather than
   * showing a family that is inactive for reasons nobody can see.
   */
  merged_into: string;
}

/**
 * What a deletion took with it. The confirmation says what will go; this
 * says what went, and the toast repeats it.
 */
export interface Deletion {
  code: string;
  depots: number;
  stock_lines: number;
  price_rules: number;
  perimeter_rows: number;
  /** Articles that lost their family. They are never deleted. */
  articles_detached: number;
}

export interface Site {
  code: string;
  name: string;
  kind: SiteKind;
  city: string;
  address: string;
  /** Visible to customers: this depot's own switch. */
  is_active: boolean;
  /** Shown in the back office: this depot's own switch. */
  back_office_visible: boolean;
  /** Hidden because its company or point of sale is, not because of its own switch. */
  hidden_by_parent_customers?: boolean;
  hidden_by_parent_back_office?: boolean;
  /** The company that owns this depot's stock. Empty until somebody says. */
  company_code: string;
  /** The place hosting this depot. Empty until somebody says. */
  point_of_sale_code: string;
  /**
   * The depot's code in the connected ERP — what a stock feed names it by.
   * Empty when it is the same as `code`: the feed falls back on that.
   */
  external_ref: string;
}

/**
 * One of the distributor's OWN companies — the subsidiaries a group trades
 * through. Not a customer: those live in the clients feature. A depot belongs
 * to exactly one, and that ownership is what an order will later be invoiced
 * under.
 */
export interface Company {
  code: string;
  name: string;
  /** Visible to customers: this company's own switch. */
  is_active: boolean;
  /** Shown in the back office: this company's own switch. */
  back_office_visible: boolean;
  hidden_by_parent_customers?: boolean;
  hidden_by_parent_back_office?: boolean;
  /** Articles in this company's perimeter — what it sells. */
  article_count?: number;
}

/** What a bulk perimeter change did. `changed` is rows actually added or removed. */
export interface PerimeterOutcome {
  company_code: string;
  matched: number;
  changed: number;
  unknown_references: string[];
  total: number;
}

/**
 * A physical location. One place can host the depots of several companies,
 * which is why it is distinct from a site: the place is shared, the stock is
 * not.
 */
export interface PointOfSale {
  code: string;
  name: string;
  city: string;
  address: string;
  specialisation: string;
  surface_m2: string | null;
  lift_count: number | null;
  headcount: number | null;
  /** Visible to customers: this point of sale's own switch. */
  is_active: boolean;
  /** Shown in the back office: this point of sale's own switch. */
  back_office_visible: boolean;
  /** Hidden because everything it hosts is hidden, not because of its own switch. */
  hidden_by_parent_customers?: boolean;
  hidden_by_parent_back_office?: boolean;
}

export type StructureView = "company" | "point_of_sale";
export type StructureLevel = "company" | "point_of_sale" | "depot";

/**
 * A node of the stock structure with the totals beneath it. An empty `code`
 * on a company or a point of sale is the pseudo-node for depots that have
 * none — kept in the tree, because a depot missing from the totals is stock
 * missing from the totals.
 */
export interface StructureNode {
  level: StructureLevel;
  code: string;
  name: string;
  city: string;
  is_active: boolean;
  depot_count: number;
  lines: number;
  real: string;
  reserved: string;
  available: string;
  children: StructureNode[];
}

export interface StructureTree {
  view: StructureView;
  items: StructureNode[];
}

/** Stable codes the screen words — see `structure.import.reason.*`. */
export type RejectionReason =
  "missing_depot_code" | "missing_point_of_sale" | "unknown_company" | "value_too_long";

export interface StructureImportReport {
  points_of_sale_created: number;
  points_of_sale_updated: number;
  depots_created: number;
  depots_updated: number;
  /** Depots left without a company, for the operator to attach. */
  depots_without_company: string[];
  /** `reason` is a `RejectionReason`, typed as a string so a code added on
   * the server later does not break the screen — it shows the raw code. */
  rejected: { row: number; reason: string; value: string }[];
}

export interface PointOfSaleInput {
  code?: string;
  name: string;
  city?: string;
  address?: string;
  specialisation?: string;
  surface_m2?: string | null;
  lift_count?: number | null;
  headcount?: number | null;
}

export interface PriceScale {
  code: string;
  label: string;
  is_active: boolean;
  /** The counter tariff lists show when none is chosen. At most one. */
  is_default?: boolean;
  /** Where it stands among the tariffs: 1 first, 2 second… */
  position?: number;
}

export interface PriceRule {
  id: string;
  scale_code: string;
  family_code: string | null;
  article_reference: string | null;
  site_code: string | null;
  discount_pct: string;
  fixed_price: string | null;
  valid_from: string | null;
  valid_to: string | null;
  visible_on_portal: boolean;
}

export interface ResolvedPrice {
  unit_price: string;
  base_price: string;
  discount_pct: string;
  /** So the back office can explain a price instead of merely quoting it. */
  applied_rule_id: string | null;
  scale_code: string | null;
}

export interface ItemPrice {
  scale_code: string;
  label: string;
  is_default: boolean;
  /** What somebody entered for this article; null when the tariff's own rules decide. */
  fixed_price: string | null;
  unit_price: string;
}

export interface ItemPriceList {
  base_price: string;
  items: ItemPrice[];
}

export interface PriceImportReport {
  rows: number;
  created: number;
  updated: number;
  unchanged: number;
  tariffs: string[];
  unmatched_columns: string[];
  unknown_references: string[];
  unknown_reference_count: number;
  rejected: { row: number; reason: string; value: string }[];
  rejected_count: number;
}

export interface FacetValue {
  value: string;
  count: number;
}

export interface Facets {
  facets: Record<string, FacetValue[]>;
}

export interface ArticleQuery {
  search?: string;
  /** The four of these that take a list: several values are alternatives. */
  family_codes?: readonly string[];
  brands?: readonly string[];
  statuses?: readonly PublicationStatus[];
  /** `key:value` pairs. Repeats on one key are an OR, different keys an AND. */
  attributes?: readonly string[];
  available_only?: boolean;
  is_active?: boolean;
  /** The companies selected. Empty or unset means every company. */
  company_codes?: readonly string[];
  after_id?: string;
  limit?: number;
}

export interface DepotSummary {
  site_code: string;
  site_name: string;
  company_code: string;
  point_of_sale_code: string;
  point_of_sale_name: string;
  city: string;
  references: number;
  real: string;
  reserved: string;
  available: string;
}

/** The depots under a set of filters, and the distinct reference counts of the places above. */
export interface StockSummary {
  depots: DepotSummary[];
  points: Record<string, number>;
  cities: Record<string, number>;
}

/** What the stock can be grouped by, once each, in the order the user chooses. */
export type StockGroupLevel = "company" | "city" | "point" | "site" | "article";

/** One line of the grouped stock: a company, a place, a depot or an article. */
export interface StockGroup {
  /** What the next level down is asked for with. Empty for « no point of sale ». */
  key: string;
  label: string;
  /** Distinct references, counted at this level rather than added from below. */
  references: number;
  depots: number;
  companies: string[];
  real: string;
  reserved: string;
  available: string;
}

export interface StockQuery {
  /** Each structure filter takes a list: several values are alternatives. */
  site_codes?: readonly string[];
  company_codes?: readonly string[];
  point_of_sale_codes?: readonly string[];
  /** A depot's place's city, or its own when it has no place. */
  cities?: readonly string[];
  search?: string;
  below_threshold_only?: boolean;
  /** Only levels with real minus reserved above zero. */
  available_only?: boolean;
  /** Only levels some unshipped order holds a quantity of. */
  reserved_only?: boolean;
  /** The article's active flag: active ones or inactive ones; absent means all. */
  article_active?: "active" | "inactive";
  /** `false` leaves out the depots the back office hid. Absent or `true` lists them all. */
  include_hidden?: boolean;
  /** A category of the web tree: only the articles at or under it. Grouped stock only. */
  category_path?: string;
  limit?: number;
  offset?: number;
}

/**
 * Build a query string, dropping what is empty.
 *
 * Empty values are omitted rather than sent as `?search=`: the backend treats
 * an empty string as a filter on the empty string in some places and as absent
 * in others, and the difference is not worth having to remember.
 */
function query(
  params: Record<string, string | number | boolean | undefined>,
  repeated: Record<string, readonly string[] | undefined> = {},
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  // A filter that takes several values repeats its key: `city=A&city=B`.
  for (const [key, values] of Object.entries(repeated)) {
    for (const value of values ?? []) if (value) search.append(key, value);
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

/**
 * `include_hidden=false`, written so it is actually sent.
 *
 * `query()` drops `false` (right for "available only", wrong here: absent means "everything"),
 * so the value goes as the string `"false"`. Nothing is added for `true`, which is the server's
 * default and what the structure screens want.
 */
function hiddenParam(includeHidden: boolean): Record<string, string> {
  return includeHidden ? {} : { include_hidden: "false" };
}

/** Every referential call, in one place. */
export class ReferentialApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  // --- Articles ------------------------------------------------------------

  /**
   * The brand list the catalogue keeps — the labels an article's brand is picked from.
   *
   * Read through this client rather than imported from the catalogue feature,
   * which a feature may not do. The caller treats a refusal (no right to read
   * the catalogue) as "no list" and falls back to typing.
   */
  async listBrandChoices(): Promise<string[]> {
    const found = await this.#client.request<{ items: { label: string }[] }>(
      "/api/v1/catalog/brands?include_inactive=false",
    );
    return found.items.map((item) => item.label);
  }

  /** Add a brand to the list. */
  createBrandChoice(label: string): Promise<{ label: string }> {
    return this.#client.request<{ label: string }>("/api/v1/catalog/brands", {
      method: "POST",
      body: { label },
    });
  }

  /** Give several articles one brand — or none, with an empty brand. */
  setArticlesBrand(articleIds: readonly string[], brand: string): Promise<{ updated: number }> {
    return this.#client.request<{ updated: number }>(`${BASE}/articles/brand`, {
      method: "POST",
      body: { article_ids: [...articleIds], brand },
    });
  }

  listArticles(options: ArticleQuery = {}): Promise<ArticleList> {
    const search = new URLSearchParams();
    if (options.search) search.set("search", options.search);
    for (const value of options.family_codes ?? []) search.append("family_code", value);
    for (const value of options.brands ?? []) search.append("brand", value);
    for (const value of options.statuses ?? []) search.append("status", value);
    if (options.available_only) search.set("available_only", "true");
    if (options.is_active !== undefined) search.set("is_active", String(options.is_active));
    for (const value of options.company_codes ?? []) search.append("company_code", value);
    if (options.after_id) search.set("after_id", options.after_id);
    search.set("limit", String(options.limit ?? 50));
    // Repeated rather than joined: the API reads `attr` several times, which
    // is what lets "rim 16 OR rim 17" be expressed at all.
    for (const pair of options.attributes ?? []) search.append("attr", pair);

    return this.#client.request<ArticleList>(`${BASE}/articles?${search.toString()}`);
  }

  facets(keys: readonly string[], limit = 40): Promise<Facets> {
    const search = new URLSearchParams();
    for (const key of keys) search.append("key", key);
    search.set("limit", String(limit));
    return this.#client.request<Facets>(`${BASE}/articles/facets?${search.toString()}`);
  }

  getArticle(id: string): Promise<ArticleDetail> {
    return this.#client.request<ArticleDetail>(`${BASE}/articles/${encodeURIComponent(id)}`);
  }

  createArticle(body: {
    reference: string;
    designation: string;
    brand?: string;
    family_code?: string;
    sales_unit?: string;
    base_price?: string;
    stock_disclosure?: StockDisclosure;
    reference_base?: string;
    code_fournisseur?: string;
    ipc?: string;
    is_active?: boolean;
  }): Promise<Article> {
    return this.#client.request<Article>(`${BASE}/articles`, { method: "POST", body });
  }

  updateArticle(
    id: string,
    body: Partial<{
      designation: string;
      brand: string;
      family_code: string;
      sales_unit: string;
      base_price: string;
      stock_disclosure: StockDisclosure;
      reference_base: string;
      code_fournisseur: string;
      ipc: string;
      is_active: boolean;
    }>,
  ): Promise<Article> {
    return this.#client.request<Article>(`${BASE}/articles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    });
  }

  /** Publish, withdraw or archive. A different permission from editing. */
  setArticleStatus(id: string, status: PublicationStatus): Promise<Article> {
    return this.#client.request<Article>(`${BASE}/articles/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: { status },
    });
  }

  /** Record a correction. It survives every later re-parse. */
  setAttribute(
    id: string,
    key: string,
    body: { value: string; is_filterable?: boolean },
  ): Promise<Article> {
    return this.#client.request<Article>(
      `${BASE}/articles/${encodeURIComponent(id)}/attributes/${encodeURIComponent(key)}`,
      { method: "PUT", body },
    );
  }

  removeAttribute(id: string, key: string): Promise<Article> {
    return this.#client.request<Article>(
      `${BASE}/articles/${encodeURIComponent(id)}/attributes/${encodeURIComponent(key)}`,
      { method: "DELETE" },
    );
  }

  reparse(id: string): Promise<Article> {
    return this.#client.request<Article>(`${BASE}/articles/${encodeURIComponent(id)}/reparse`, {
      method: "POST",
    });
  }

  price(id: string, options: { scale?: string; site?: string } = {}): Promise<ResolvedPrice> {
    return this.#client.request<ResolvedPrice>(
      `${BASE}/articles/${encodeURIComponent(id)}/price${query(options)}`,
    );
  }

  /** One article's price under every tariff: a row each, the amounts its own. */
  itemPrices(id: string): Promise<ItemPriceList> {
    return this.#client.request<ItemPriceList>(`${BASE}/articles/${encodeURIComponent(id)}/prices`);
  }

  /** Set the article's price under one tariff; `null` removes it. */
  setItemPrice(id: string, scaleCode: string, price: string | null): Promise<ItemPriceList> {
    return this.#client.request<ItemPriceList>(
      `${BASE}/articles/${encodeURIComponent(id)}/prices/${encodeURIComponent(scaleCode)}`,
      { method: "PUT", body: { price } },
    );
  }

  importPrices(file: File): Promise<PriceImportReport> {
    const form = new FormData();
    form.set("upload", file);
    return this.#client.upload<PriceImportReport>(`${BASE}/prices/import`, form);
  }

  importScales(file: File): Promise<{ created: string[]; existing: string[] }> {
    const form = new FormData();
    form.set("upload", file);
    return this.#client.upload<{ created: string[]; existing: string[] }>(
      `${BASE}/price-scales/import`,
      form,
    );
  }

  exportPrices(): Promise<Blob> {
    return this.#client.requestBlob(`${BASE}/prices/export`);
  }

  // --- Families ------------------------------------------------------------

  listFamilies(includeInactive = true): Promise<{ items: Family[] }> {
    return this.#client.request<{ items: Family[] }>(
      `${BASE}/families${query({ include_inactive: includeInactive })}`,
    );
  }

  /**
   * Fold one family into another.
   *
   * The delivered referential has four families for one product line
   * (`LUBRIFIANT`, `LUBRIFIANTS`, `LUBRIFIANTS1`, `Lubrifiant`), and the
   * source survives as a redirection so the next import does not undo the
   * correction. See the backend's route for why.
   */
  mergeFamily(code: string, into: string): Promise<MergeOutcome> {
    return this.#client.request<MergeOutcome>(
      `${BASE}/families/${encodeURIComponent(code)}/merge`,
      { method: "POST", body: { into } },
    );
  }

  createFamily(body: {
    code: string;
    label?: string;
    default_unit?: string;
    required_attributes?: string[];
  }): Promise<Family> {
    return this.#client.request<Family>(`${BASE}/families`, { method: "POST", body });
  }

  updateFamily(
    code: string,
    body: Partial<{
      label: string;
      default_unit: string;
      required_attributes: string[];
      is_active: boolean;
    }>,
  ): Promise<Family> {
    return this.#client.request<Family>(`${BASE}/families/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body,
    });
  }

  /** For good: its articles stay and lose their family. A merge keeps a redirection. */
  deleteFamily(code: string): Promise<Deletion> {
    return this.#client.request<Deletion>(`${BASE}/families/${encodeURIComponent(code)}`, {
      method: "DELETE",
    });
  }

  // --- Sites ---------------------------------------------------------------

  /**
   * `includeInactive = false` leaves out what is hidden from customers; `includeHidden = false`
   * leaves out what the back office hid (a depot of a hidden company counts). The structure
   * screens want everything, so both default to true; the pickers pass `(true, false)`.
   */
  listSites(includeInactive = true, includeHidden = true): Promise<{ items: Site[] }> {
    return this.#client.request<{ items: Site[] }>(
      `${BASE}/sites${query({ include_inactive: includeInactive, ...hiddenParam(includeHidden) })}`,
    );
  }

  createSite(body: {
    code: string;
    name: string;
    kind?: SiteKind;
    city?: string;
    address?: string;
    company_code?: string;
    point_of_sale_code?: string;
    external_ref?: string;
  }): Promise<Site> {
    return this.#client.request<Site>(`${BASE}/sites`, { method: "POST", body });
  }

  updateSite(
    code: string,
    body: Partial<{
      name: string;
      kind: SiteKind;
      city: string;
      address: string;
      is_active: boolean;
      back_office_visible: boolean;
      /** `""` detaches the depot from its company. */
      company_code: string;
      /** `""` detaches the depot from its point of sale. */
      point_of_sale_code: string;
      /** `""` clears the ERP code: the feed then matches on `code`. */
      external_ref: string;
    }>,
  ): Promise<Site> {
    return this.#client.request<Site>(`${BASE}/sites/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body,
    });
  }

  /** The depot, its stock lines and the price rules scoped to it. */
  deleteSite(code: string): Promise<Deletion> {
    return this.#client.request<Deletion>(`${BASE}/sites/${encodeURIComponent(code)}`, {
      method: "DELETE",
    });
  }

  // --- Stock structure -----------------------------------------------------

  listCompanies(includeInactive = true, includeHidden = true): Promise<{ items: Company[] }> {
    return this.#client.request<{ items: Company[] }>(
      `${BASE}/companies${query({ include_inactive: includeInactive, ...hiddenParam(includeHidden) })}`,
    );
  }

  createCompany(body: { code: string; name?: string }): Promise<Company> {
    return this.#client.request<Company>(`${BASE}/companies`, { method: "POST", body });
  }

  updateCompany(
    code: string,
    body: Partial<{ name: string; is_active: boolean; back_office_visible: boolean }>,
  ): Promise<Company> {
    return this.#client.request<Company>(`${BASE}/companies/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body,
    });
  }

  /** The company, its depots with their stock, and its perimeter. */
  deleteCompany(code: string): Promise<Deletion> {
    return this.#client.request<Deletion>(`${BASE}/companies/${encodeURIComponent(code)}`, {
      method: "DELETE",
    });
  }

  /** Add articles to a company's perimeter, or take them out, by family or reference. */
  changePerimeter(
    code: string,
    body: { family_codes?: string[]; references?: string[]; remove?: boolean },
  ): Promise<PerimeterOutcome> {
    return this.#client.request<PerimeterOutcome>(
      `${BASE}/companies/${encodeURIComponent(code)}/perimeter`,
      { method: "POST", body },
    );
  }

  articleCompanies(id: string): Promise<{ article_id: string; company_codes: string[] }> {
    return this.#client.request<{ article_id: string; company_codes: string[] }>(
      `${BASE}/articles/${encodeURIComponent(id)}/companies`,
    );
  }

  setArticleCompanies(
    id: string,
    companyCodes: string[],
  ): Promise<{ article_id: string; company_codes: string[] }> {
    return this.#client.request<{ article_id: string; company_codes: string[] }>(
      `${BASE}/articles/${encodeURIComponent(id)}/companies`,
      { method: "PUT", body: { company_codes: companyCodes } },
    );
  }

  listPointsOfSale(
    includeInactive = true,
    includeHidden = true,
  ): Promise<{ items: PointOfSale[] }> {
    return this.#client.request<{ items: PointOfSale[] }>(
      `${BASE}/points-of-sale${query({ include_inactive: includeInactive, ...hiddenParam(includeHidden) })}`,
    );
  }

  /** The code is derived from the name when omitted, as the import derives it. */
  createPointOfSale(body: PointOfSaleInput): Promise<PointOfSale> {
    return this.#client.request<PointOfSale>(`${BASE}/points-of-sale`, { method: "POST", body });
  }

  updatePointOfSale(
    code: string,
    body: Partial<
      Omit<PointOfSaleInput, "code"> & { is_active: boolean; back_office_visible: boolean }
    >,
  ): Promise<PointOfSale> {
    return this.#client.request<PointOfSale>(`${BASE}/points-of-sale/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body,
    });
  }

  /** The point of sale, and the depots it hosts with their stock. */
  deletePointOfSale(code: string): Promise<Deletion> {
    return this.#client.request<Deletion>(`${BASE}/points-of-sale/${encodeURIComponent(code)}`, {
      method: "DELETE",
    });
  }

  /** Company → point of sale → depot, or point of sale → company → depot. */
  stockStructure(view: StructureView = "company", includeHidden = true): Promise<StructureTree> {
    return this.#client.request<StructureTree>(
      `${BASE}/stock-structure${query({ by: view, ...hiddenParam(includeHidden) })}`,
    );
  }

  /**
   * Load points of sale and depots from a spreadsheet, synchronously.
   *
   * Through the client's `upload`, which sends `FormData` without a
   * `Content-Type` so the browser writes the multipart boundary — a file sent
   * as a JSON `body` would arrive as `{}`.
   */
  importStructure(file: File): Promise<StructureImportReport> {
    const form = new FormData();
    form.append("upload", file, file.name);
    return this.#client.upload<StructureImportReport>(`${BASE}/stock-structure/import`, form);
  }

  // --- Stock ---------------------------------------------------------------

  listStock(options: StockQuery = {}): Promise<{ items: StockLevel[] }> {
    const { site_codes, company_codes, point_of_sale_codes, cities, include_hidden, ...rest } =
      options;
    return this.#client.request<{ items: StockLevel[] }>(
      `${BASE}/stock${query(
        { ...rest, ...hiddenParam(include_hidden ?? true) },
        {
          site_code: site_codes,
          company_code: company_codes,
          point_of_sale_code: point_of_sale_codes,
          city: cities,
        },
      )}`,
    );
  }

  /**
   * One level of the stock, in the order the user chose, inside the groups already opened.
   *
   * `path` is built by hand: `query()` drops empty values, and an empty key is a real group
   * — « no point of sale » — that must reach the server.
   */
  stockGroups(
    levels: readonly StockGroupLevel[],
    path: readonly string[],
    options: StockQuery = {},
  ): Promise<{ items: StockGroup[] }> {
    const { site_codes, company_codes, point_of_sale_codes, cities, include_hidden, ...rest } =
      options;
    const search = new URLSearchParams(
      query(
        { ...rest, ...hiddenParam(include_hidden ?? true) },
        {
          site_code: site_codes,
          company_code: company_codes,
          point_of_sale_code: point_of_sale_codes,
          city: cities,
        },
      ).slice(1),
    );
    for (const level of levels) search.append("level", level);
    for (const key of path) search.append("path", key);
    return this.#client.request<{ items: StockGroup[] }>(`${BASE}/stock/groups?${search}`);
  }

  /** How this user arranges their screens: screen key → the levels they nest, in order. */
  async layouts(): Promise<Record<string, string[]>> {
    const answer = await this.#client.request<{ layouts?: Record<string, string[]> } | null>(
      "/api/v1/iam/me/layouts",
    );
    const saved = answer?.layouts;
    return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  }

  saveLayout(key: string, levels: readonly string[]): Promise<unknown> {
    return this.#client.request(`/api/v1/iam/me/layouts/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: { levels },
    });
  }

  /** The depots with their reference counts, for the grouped view — the list's filters. */
  stockSummary(options: StockQuery = {}): Promise<StockSummary> {
    const { site_codes, company_codes, point_of_sale_codes, cities, include_hidden, ...rest } =
      options;
    return this.#client.request<StockSummary>(
      `${BASE}/stock/summary${query(
        { ...rest, ...hiddenParam(include_hidden ?? true) },
        {
          site_code: site_codes,
          company_code: company_codes,
          point_of_sale_code: point_of_sale_codes,
          city: cities,
        },
      )}`,
    );
  }

  /**
   * Set on-hand, forecast or alert threshold at one site.
   *
   * There is no parameter for `reserved`, and there must not be: it is derived
   * from this portal's own unshipped orders, and a typed figure would put what
   * customers see permanently out of step with what they ordered.
   */
  setStock(
    articleId: string,
    siteCode: string,
    body: Partial<{ real: string; forecast: string; alert_threshold: string }>,
  ): Promise<{ items: StockLevel[] }> {
    return this.#client.request<{ items: StockLevel[] }>(
      `${BASE}/articles/${encodeURIComponent(articleId)}/stock/${encodeURIComponent(siteCode)}`,
      { method: "PUT", body },
    );
  }

  // --- Pricing -------------------------------------------------------------

  listScales(includeInactive = true): Promise<{ items: PriceScale[] }> {
    return this.#client.request<{ items: PriceScale[] }>(
      `${BASE}/price-scales${query({ include_inactive: includeInactive })}`,
    );
  }

  createScale(body: { code: string; label?: string }): Promise<PriceScale> {
    return this.#client.request<PriceScale>(`${BASE}/price-scales`, { method: "POST", body });
  }

  /** Put the tariffs in this order, first to last. */
  orderScales(codes: string[]): Promise<{ items: PriceScale[] }> {
    return this.#client.request<{ items: PriceScale[] }>(`${BASE}/price-scales-order`, {
      method: "PUT",
      body: { codes },
    });
  }

  /** Make this tariff the default; the previous default stops being one. */
  setDefaultScale(code: string): Promise<PriceScale> {
    return this.#client.request<PriceScale>(
      `${BASE}/price-scales/${encodeURIComponent(code)}/default`,
      { method: "PUT" },
    );
  }

  listRules(scaleCode: string): Promise<{ items: PriceRule[] }> {
    return this.#client.request<{ items: PriceRule[] }>(
      `${BASE}/price-scales/${encodeURIComponent(scaleCode)}/rules`,
    );
  }

  createRule(
    scaleCode: string,
    body: Partial<{
      family_code: string | null;
      article_reference: string | null;
      site_code: string | null;
      discount_pct: string;
      fixed_price: string | null;
      valid_from: string | null;
      valid_to: string | null;
      visible_on_portal: boolean;
    }>,
  ): Promise<PriceRule> {
    return this.#client.request<PriceRule>(
      `${BASE}/price-scales/${encodeURIComponent(scaleCode)}/rules`,
      { method: "POST", body },
    );
  }

  deleteRule(id: string): Promise<void> {
    return this.#client.request<void>(`${BASE}/price-rules/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }
}
